/**
 * Tool Integration Service
 * 
 * Connects prioritization tools with:
 * 1. Shaped items from the candidates store (input)
 * 2. Focus lists for saving results (output)
 * 3. Priority snapshots for committing decisions
 * 
 * This enables the integrated workflow:
 * - Tools pull items that are ready for ranking
 * - Users run prioritization tools
 * - Results can be saved to focus lists
 * - Snapshots capture the decision for audit
 */

import type { Item, Priority } from '../types/prioritization/lifecycle';

// ============================================================================
// Types
// ============================================================================

/**
 * An item prepared for tool input
 * Contains only the data tools need to work with
 */
export interface ToolInputItem {
  id: string;
  title: string;
  description?: string;
  // eslint-disable-next-line @typescript-eslint/no-explicit-any -- pre-existing, unrelated to this change
  attributes: Record<string, any>;  // Flattened for easy access
  
  // Metadata
  createdAt: Date;
  createdBy: string;
  orgUnitId?: string;
  tags: string[];
  
  // For scoring tools
  scores?: Record<string, number>;
}

/**
 * Result from a tool run for a single item
 */
export interface ToolItemResult {
  itemId: string;
  
  // Scoring
  score?: number;
  normalizedScore?: number;  // 0-100 scale
  
  // Ranking
  rank?: number;
  tier?: string;  // e.g., "Must Have", "Quadrant A", etc.
  
  // Classification
  category?: string;
  labels?: string[];
  
  // Tool-specific data
  // eslint-disable-next-line @typescript-eslint/no-explicit-any -- pre-existing, unrelated to this change
  toolData?: Record<string, any>;
}

/**
 * Complete result from a tool run
 */
export interface ToolRunResult {
  // Tool identification
  toolId: string;
  toolName: string;
  toolVersion?: string;
  
  // Input
  inputItemIds: string[];
  inputCriteriaIds?: string[];
  
  // Output
  results: ToolItemResult[];
  
  // Ordering
  rankedItemIds?: string[];  // Items in rank order
  
  // Metadata
  runAt: Date;
  runBy: string;
  durationMs?: number;
  
  // Configuration used
  // eslint-disable-next-line @typescript-eslint/no-explicit-any -- pre-existing, unrelated to this change
  config?: Record<string, any>;
}

/**
 * Options for loading items for a tool
 */
export interface LoadItemsOptions {
  // Filter by status
  readyForRanking?: boolean;
  
  // Filter by org unit
  orgUnitId?: string;
  orgUnitIds?: string[];
  
  // Filter by tags
  tags?: string[];
  
  // Filter by attributes
  requiredAttributes?: string[];
  
  // Limit
  limit?: number;
  
  // Include related data
  includeScores?: boolean;
}

/**
 * Options for saving results to an item pool
 */
export interface SaveToListOptions {
  // Target item pool (create new if not provided)
  itemPoolId?: string;
  
  // For new list
  listName?: string;
  listDescription?: string;
  
  // Cut line position
  cutLinePosition?: number;
  cutLineByScore?: number;  // Score threshold
  cutLineByRank?: number;   // Top N items
  
  // Merge behavior
  mergeMode?: 'replace' | 'append' | 'merge';
  
  // Create snapshot?
  createSnapshot?: boolean;
  snapshotNote?: string;
}

/**
 * Result of saving to an item pool
 */
export interface SaveToListResult {
  success: boolean;
  itemPoolId: string;
  itemPoolName: string;
  
  // Stats
  itemsAdded: number;
  itemsUpdated: number;
  itemsRemoved: number;
  
  // Snapshot
  snapshotId?: string;
  
  // Errors
  errors?: string[];
}

// ============================================================================
// Item Transformation
// ============================================================================

/**
 * Transform an Item from the candidates store to a ToolInputItem
 */
export function itemToToolInput(item: Item): ToolInputItem {
  // Flatten attributes for easy tool access
  // eslint-disable-next-line @typescript-eslint/no-explicit-any -- pre-existing, unrelated to this change
  const attributes: Record<string, any> = {};
  if (item.attributes && Array.isArray(item.attributes)) {
    for (const av of item.attributes) {
      attributes[av.attributeKey] = av.value;
    }
  }
  
  return {
    id: item.id,
    title: item.title,
    description: item.description,
    attributes,
    createdAt: item.createdAt,
    createdBy: item.createdBy,
    orgUnitId: item.orgUnitId,
    tags: item.tags || [],
  };
}

/**
 * Transform multiple items
 */
export function itemsToToolInput(items: Item[]): ToolInputItem[] {
  return items.map(itemToToolInput);
}

/**
 * Get attribute value by key with type safety
 */
export function getAttributeValue<T>(
  item: ToolInputItem,
  key: string,
  defaultValue: T
): T {
  const value = item.attributes[key];
  if (value === undefined || value === null) return defaultValue;
  return value as T;
}

/**
 * Get numeric attribute (for scoring)
 */
export function getNumericAttribute(
  item: ToolInputItem,
  key: string,
  defaultValue: number = 0
): number {
  const value = item.attributes[key];
  if (typeof value === 'number') return value;
  if (typeof value === 'string') {
    const parsed = parseFloat(value);
    return isNaN(parsed) ? defaultValue : parsed;
  }
  return defaultValue;
}

// ============================================================================
// Tool Run Helpers
// ============================================================================

/**
 * Create a new tool run result
 */
export function createToolRunResult(
  toolId: string,
  toolName: string,
  items: ToolInputItem[],
  runBy: string
): ToolRunResult {
  return {
    toolId,
    toolName,
    inputItemIds: items.map(i => i.id),
    results: [],
    runAt: new Date(),
    runBy,
  };
}

/**
 * Add item result to a tool run
 */
export function addItemResult(
  toolRun: ToolRunResult,
  itemId: string,
  result: Omit<ToolItemResult, 'itemId'>
): void {
  toolRun.results.push({
    itemId,
    ...result,
  });
}

/**
 * Set ranked order in tool run
 */
export function setRankedOrder(
  toolRun: ToolRunResult,
  rankedItemIds: string[]
): void {
  toolRun.rankedItemIds = rankedItemIds;
  
  // Update ranks in results
  rankedItemIds.forEach((itemId, index) => {
    const result = toolRun.results.find(r => r.itemId === itemId);
    if (result) {
      result.rank = index + 1;
    }
  });
}

/**
 * Sort results by score (descending)
 */
export function sortResultsByScore(toolRun: ToolRunResult): void {
  toolRun.results.sort((a, b) => (b.score || 0) - (a.score || 0));
  toolRun.rankedItemIds = toolRun.results.map(r => r.itemId);
  
  // Update ranks
  toolRun.results.forEach((result, index) => {
    result.rank = index + 1;
  });
}

/**
 * Normalize scores to 0-100 scale
 */
export function normalizeScores(toolRun: ToolRunResult): void {
  const scores = toolRun.results
    .map(r => r.score)
    .filter((s): s is number => s !== undefined);
  
  if (scores.length === 0) return;
  
  const min = Math.min(...scores);
  const max = Math.max(...scores);
  const range = max - min;
  
  for (const result of toolRun.results) {
    if (result.score !== undefined) {
      result.normalizedScore = range > 0
        ? ((result.score - min) / range) * 100
        : 50;  // All same score = 50
    }
  }
}

// ============================================================================
// Focus List Integration
// ============================================================================

/**
 * Prepare tool results for saving to an item pool
 * Returns priorities ready to be added to a list
 */
export function resultsToListItems(
  toolRun: ToolRunResult,
  itemsById: Map<string, Item>,
  itemPoolId: string
): Omit<Priority, 'lifecycle_state' | 'lifecycle_stage'>[] {
  const rankedIds = toolRun.rankedItemIds || toolRun.results.map(r => r.itemId);
  
  return rankedIds.map((itemId, index) => {
    const item = itemsById.get(itemId);
    const result = toolRun.results.find(r => r.itemId === itemId);
    
    if (!item) {
      throw new Error(`Item ${itemId} not found`);
    }
    
    return {
      id: item.id,
      title: item.title,
      description: item.description,
      attributes: item.attributes,
      // Priority/ShapedItem require these non-optional; Item carries them as
      // an optional legacy mirror (see types/prioritization/lifecycle.ts).
      // Same fallback convention as itemsStore.ts's own reads of these fields.
      shapingCompleteness: item.shapingCompleteness ?? 0,
      readyForRanking: item.readyForRanking ?? false,
      createdAt: item.createdAt,
      updatedAt: new Date(),
      createdBy: item.createdBy,
      shapingOwnerId: item.shapingOwnerId ?? item.stewardId,
      source: item.source,
      tags: item.tags,
      metadata: {
        ...item.metadata,
        toolRun: {
          toolId: toolRun.toolId,
          toolName: toolRun.toolName,
          runAt: toolRun.runAt.toISOString(),
          score: result?.score,
          normalizedScore: result?.normalizedScore,
          tier: result?.tier,
        },
      },
      orgUnitId: item.orgUnitId,
      orgUnitPath: item.orgUnitPath,
      itemPoolId,
      rank: index + 1,
      aboveTheLine: true,  // Will be set by cutline logic
      scores: result?.score ? { [toolRun.toolId]: result.score } : undefined,
      slottingRationale: result?.tier 
        ? `Placed by ${toolRun.toolName}: ${result.tier}`
        : `Ranked #${index + 1} by ${toolRun.toolName}`,
    };
  });
}

/**
 * Calculate cut line position based on options
 */
export function calculateCutLine(
  toolRun: ToolRunResult,
  options: SaveToListOptions
): number {
  // Explicit position
  if (options.cutLinePosition !== undefined) {
    return options.cutLinePosition;
  }
  
  // By score threshold
  if (options.cutLineByScore !== undefined) {
    const aboveThreshold = toolRun.results.filter(r => 
      (r.normalizedScore || r.score || 0) >= options.cutLineByScore!
    );
    return aboveThreshold.length;
  }
  
  // By rank (top N)
  if (options.cutLineByRank !== undefined) {
    return Math.min(options.cutLineByRank, toolRun.results.length);
  }
  
  // Default: all items above the line
  return toolRun.results.length;
}

// ============================================================================
// Tool Registry
// ============================================================================

/**
 * Tool definition for the registry
 */
export interface ToolDefinition {
  id: string;
  name: string;
  description: string;
  category: 'framework' | 'scoring' | 'ranking' | 'collaborative';
  
  // What this tool needs
  requiresAttributes?: string[];
  minItems?: number;
  maxItems?: number;
  
  // What this tool produces
  producesScore: boolean;
  producesRanking: boolean;
  producesTiers: boolean;
  
  // Path in app
  path: string;
}

/**
 * Registry of all prioritization tools
 */
export const TOOL_REGISTRY: ToolDefinition[] = [
  // Frameworks
  {
    id: 'custom-matrix',
    name: 'Custom 2×2 Matrix',
    description: 'Build and save your own named 2×2 matrix',
    category: 'framework',
    producesScore: false,
    producesRanking: false,
    producesTiers: true,
    path: '/prioritization/tools/custom-matrix',
  },
  {
    id: 'eisenhower-matrix',
    name: 'Eisenhower Matrix',
    description: 'Urgent vs Important 2×2 matrix',
    category: 'framework',
    producesScore: false,
    producesRanking: false,
    producesTiers: true,
    path: '/prioritization/tools/eisenhower-matrix',
  },
  {
    id: 'moscow-method',
    name: 'MoSCoW Method',
    description: 'Must, Should, Could, Won\'t categorization',
    category: 'framework',
    producesScore: false,
    producesRanking: false,
    producesTiers: true,
    path: '/prioritization/tools/moscow-method',
  },
  {
    id: 'impact-effort-matrix',
    name: 'Impact/Effort Matrix',
    description: 'Plot items by value and cost',
    category: 'framework',
    requiresAttributes: ['impact', 'effort'],
    producesScore: true,
    producesRanking: true,
    producesTiers: true,
    path: '/prioritization/tools/impact-effort-matrix',
  },
  
  // Scoring
  {
    id: 'rice-scoring',
    name: 'RICE Scoring',
    description: 'Reach × Impact × Confidence ÷ Effort',
    category: 'scoring',
    requiresAttributes: ['reach', 'impact', 'confidence', 'effort'],
    producesScore: true,
    producesRanking: true,
    producesTiers: false,
    path: '/prioritization/tools/rice-scoring',
  },
  {
    id: 'wsjf',
    name: 'WSJF',
    description: 'Weighted Shortest Job First (SAFe)',
    category: 'scoring',
    requiresAttributes: ['business_value', 'time_criticality', 'risk_reduction', 'job_size'],
    producesScore: true,
    producesRanking: true,
    producesTiers: false,
    path: '/prioritization/tools/wsjf',
  },
  {
    id: 'cost-of-delay',
    name: 'Cost of Delay',
    description: 'Time-based value calculations',
    category: 'scoring',
    requiresAttributes: ['value_per_period', 'duration'],
    producesScore: true,
    producesRanking: true,
    producesTiers: false,
    path: '/prioritization/tools/cost-of-delay',
  },
  {
    id: 'magic-prioritization',
    name: 'Magic Prioritization',
    description: 'Staged decoupling to surface CD3',
    category: 'scoring',
    producesScore: true,
    producesRanking: true,
    producesTiers: false,
    path: '/prioritization/tools/magic-prioritization',
  },
  
  // Ranking
  {
    id: 'stack-ranking',
    name: 'Stack Ranking',
    description: 'Forced linear ordering',
    category: 'ranking',
    producesScore: false,
    producesRanking: true,
    producesTiers: false,
    path: '/prioritization/tools/stack-ranking',
  },
  {
    id: 'pairwise-ranking',
    name: 'Pairwise Comparison',
    description: 'Compare items two at a time',
    category: 'ranking',
    maxItems: 20,  // Gets expensive with too many items
    producesScore: true,
    producesRanking: true,
    producesTiers: false,
    path: '/prioritization/tools/pairwise-ranking',
  },
  {
    id: 'ahp',
    name: 'AHP',
    description: 'Analytic Hierarchy Process',
    category: 'ranking',
    maxItems: 15,
    producesScore: true,
    producesRanking: true,
    producesTiers: false,
    path: '/prioritization/tools/ahp',
  },
  
  // Collaborative
  {
    id: 'dot-voting',
    name: 'Dot Voting',
    description: 'Distribute votes across options',
    category: 'collaborative',
    producesScore: true,
    producesRanking: true,
    producesTiers: false,
    path: '/prioritization/tools/dot-voting',
  },
  {
    id: 'buy-a-feature',
    name: 'Buy A Feature',
    description: 'Allocate budget to priorities',
    category: 'collaborative',
    producesScore: true,
    producesRanking: true,
    producesTiers: false,
    path: '/prioritization/tools/buy-a-feature',
  },
  {
    id: 'speed-boat',
    name: 'Speed Boat',
    description: 'Identify anchors and engines',
    category: 'collaborative',
    producesScore: false,
    producesRanking: false,
    producesTiers: true,
    path: '/prioritization/tools/speed-boat',
  },
  {
    id: 'prune-product-tree',
    name: 'Prune the Tree',
    description: 'Trim features from product tree',
    category: 'collaborative',
    producesScore: false,
    producesRanking: false,
    producesTiers: true,
    path: '/prioritization/tools/prune-product-tree',
  },
  {
    id: 'relationship-scenario-planner',
    name: 'Relationship Scenario Planner',
    description: 'Drag items to explore what-if orderings and surface package and dependency constraints inline',
    category: 'ranking',
    producesScore: false,
    producesRanking: false,
    producesTiers: false,
    path: '/prioritization/tools/relationship-scenario-planner',
  },
];

/**
 * Get tool by ID
 */
export function getToolById(toolId: string): ToolDefinition | undefined {
  return TOOL_REGISTRY.find(t => t.id === toolId);
}

/**
 * Get tools by category
 */
export function getToolsByCategory(category: ToolDefinition['category']): ToolDefinition[] {
  return TOOL_REGISTRY.filter(t => t.category === category);
}

/**
 * Check if items have required attributes for a tool
 */
export function validateItemsForTool(
  items: ToolInputItem[],
  tool: ToolDefinition
): { valid: boolean; errors: string[] } {
  const errors: string[] = [];
  
  // Check item count
  if (tool.minItems && items.length < tool.minItems) {
    errors.push(`Tool requires at least ${tool.minItems} items, got ${items.length}`);
  }
  if (tool.maxItems && items.length > tool.maxItems) {
    errors.push(`Tool supports at most ${tool.maxItems} items, got ${items.length}`);
  }
  
  // Check required attributes
  if (tool.requiresAttributes) {
    for (const attr of tool.requiresAttributes) {
      const missingItems = items.filter(item => 
        item.attributes[attr] === undefined || item.attributes[attr] === null
      );
      if (missingItems.length > 0) {
        errors.push(`${missingItems.length} items missing required attribute: ${attr}`);
      }
    }
  }
  
  return {
    valid: errors.length === 0,
    errors,
  };
}
